import React from "react"
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  DatePicker,
  FormInstance,
  Divider,
} from "antd"
import { Rule } from "antd/es/form"
import {
  ScriptParameterType,
  ScriptMetadata,
  FormParameterValues,
} from "types/automation.types"
import { useProjectsQuery } from "features/Projects/api/useProjectsQuery"
import { PROJECT_FILTER } from "types/project.types"

const { TextArea } = Input
const { Option } = Select

interface DynamicParameterFormProps {
  parameters: ScriptMetadata["parameters"]
  form: FormInstance<FormParameterValues>
  onValuesChange?: (
    changedValues: Partial<FormParameterValues>,
    allValues: FormParameterValues
  ) => void
}

const DynamicParameterForm: React.FC<DynamicParameterFormProps> = ({
  parameters,
  form,
  onValuesChange,
}) => {
  // Always fetch projects since the output project field is always shown.
  const projectsQuery = useProjectsQuery(PROJECT_FILTER.ALL)
  const projects = projectsQuery.data ?? []
  const fetchingProjects = projectsQuery.isFetching

  const renderFormField = (parameter: ScriptMetadata["parameters"][0]) => {
    const {
      name,
      type,
      required = false,
      help_text,
      validation_pattern,
      default_value,
      options = [],
      allow_multiple_projects = false,
    } = parameter

    // Build validation rules
    const rules: Rule[] = []
    if (required) {
      rules.push({
        required: true,
        message: `${name} is required`,
      })
    }
    if (validation_pattern) {
      rules.push({
        pattern: new RegExp(validation_pattern),
        message: `${name} does not match the required pattern`,
      })
    }

    // Common form item props
    const formItemProps = {
      key: name,
      name,
      label: name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      rules,
      tooltip: help_text,
      initialValue: default_value,
    }

    switch (type) {
      case ScriptParameterType.TEXT:
        return (
          <Form.Item {...formItemProps}>
            <Input placeholder={`Enter ${name}`} />
          </Form.Item>
        )

      case ScriptParameterType.NUMBER:
        return (
          <Form.Item {...formItemProps}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Enter ${name}`}
            />
          </Form.Item>
        )

      case ScriptParameterType.BOOLEAN:
        return (
          <Form.Item {...formItemProps} valuePropName="checked">
            <Switch style={{ backgroundColor: "#36c3ed" }} />
          </Form.Item>
        )

      case ScriptParameterType.DROPDOWN:
        return (
          <Form.Item {...formItemProps}>
            <Select placeholder={`Select ${name}`}>
              {options.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )

      case ScriptParameterType.PROJECT_ID:
        return (
          <Form.Item {...formItemProps}>
            <Select
              mode={allow_multiple_projects ? "multiple" : undefined}
              placeholder={`Select ${name}`}
              loading={fetchingProjects}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children || "")
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              maxTagCount={3}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} more`
              }
              // For multiple selections, let Ant Design handle the array naturally
              // The form will get an array of selected IDs
            >
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name} ({project.owner})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )

      case ScriptParameterType.FILE_PATH:
        return (
          <Form.Item {...formItemProps}>
            <Input placeholder={`Enter file path for ${name}`} />
          </Form.Item>
        )

      case ScriptParameterType.DATE:
        return (
          <Form.Item {...formItemProps}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        )

      case ScriptParameterType.MULTITEXT:
        return (
          <Form.Item {...formItemProps}>
            <TextArea
              rows={4}
              placeholder={`Enter ${name} (multiple lines allowed)`}
            />
          </Form.Item>
        )

      default:
        return (
          <Form.Item {...formItemProps}>
            <Input placeholder={`Enter ${name}`} />
          </Form.Item>
        )
    }
  }

  /**
   * Render the always-present Output Project field
   */
  const renderOutputProjectField = () => {
    return (
      <Form.Item
        name="output_project"
        label="Output Project"
        rules={[
          {
            required: true,
            message: "Output project is required",
          },
        ]}
        tooltip="Select the project where the script output will be stored"
      >
        <Select
          placeholder="Select output project"
          loading={fetchingProjects}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            String(option?.children || "")
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {projects.map((project) => (
            <Option key={project.id} value={project.id}>
              {project.name} ({project.owner})
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={onValuesChange}
      className="mt-4"
    >
      {/* Always show Output Project field first */}
      {renderOutputProjectField()}

      {/* Show divider only if there are additional parameters */}
      {parameters && parameters.length > 0 && (
        <Divider className="my-4">Script Parameters</Divider>
      )}

      {/* Render script-specific parameters */}
      {parameters && parameters.length > 0 ? (
        parameters.map((parameter) => renderFormField(parameter))
      ) : (
        <div className="text-center py-4 text-gray-500">
          No additional parameters required for this script
        </div>
      )}
    </Form>
  )
}

export default DynamicParameterForm
